// Tela de retenção do Portal (#5832, projeto 11).
//
// O cliente vê o que já ganhou antes de pedir o cancelamento. A tela informa,
// não segura: seguir para o pedido continua a um clique.
//
// ⚠️ Textos PROVISÓRIOS, baseados no que o Vinicius Alves mandou no grupo. A
// aprovação é a demanda 01 — por isso TEXTO_PROVISORIO abaixo, do mesmo jeito
// que em mensagemRetornoCancelamento.ts.
//
// Sem I/O nas funções puras; o registro dos passos fica no fim do arquivo.

export const TEXTO_PROVISORIO = true;

export type EventoRetencao = 'viu' | 'seguiu' | 'desistiu' | 'falar_com_vigor';

export interface FaturaComEconomia {
  economia?: number | null;
  custo_s_vigor?: number | null;
  valor_fatura_distribuidora?: number | null;
  valor_boleto_vigor?: number | null;
  mes_referencia?: string | null;
}

export interface ResumoEconomia {
  acumulada: number;
  meses: number;
  media: number | null;
  /** Sem economia registrada: a tela mostra a versão sem número. */
  temEconomia: boolean;
}

/**
 * Economia de uma fatura, no mesmo critério da tela inicial do Portal
 * (views/PortalConsumidor/format.ts): vale o campo gravado na emissão e, quando
 * ele está zerado, a conta pelo custo sem a Vigor.
 *
 * Repetido aqui de propósito: este arquivo é puro e testável, e a retenção não
 * pode mostrar um número diferente do que o cliente vê na home.
 */
export function economiaDaFatura(f: FaturaComEconomia): number | null {
  if (typeof f.economia === 'number' && f.economia > 0) return f.economia;
  if (typeof f.custo_s_vigor === 'number' && f.custo_s_vigor > 0) {
    const pago = Number(f.valor_fatura_distribuidora ?? 0) + Number(f.valor_boleto_vigor ?? 0);
    const economia = f.custo_s_vigor - pago;
    return economia > 0 ? economia : null;
  }
  return null;
}

export function resumoEconomia(faturas: FaturaComEconomia[] | null | undefined): ResumoEconomia {
  const valores = (faturas || []).map(economiaDaFatura).filter((v): v is number => v !== null && v > 0);
  const acumulada = Math.round(valores.reduce((s, v) => s + v, 0) * 100) / 100;
  const meses = valores.length;
  return {
    acumulada,
    meses,
    media: meses > 0 ? Math.round((acumulada / meses) * 100) / 100 : null,
    temEconomia: acumulada > 0,
  };
}

export interface PontoRetencao {
  titulo: string;
  texto: string;
}

/**
 * O que a tela diz ao cliente. Com economia, o primeiro ponto é o dinheiro que
 * ele já ganhou; sem economia (87 das 671 UCs ativas em 24/09/2026), entra o
 * desconto atual — mostrar "R$ 0,00" empurraria o cliente para fora.
 */
/**
 * `comAvisoPrevio: false` tira o ponto dos 90 dias: é regra do contrato da Vigor e não vale
 * para cliente de empresa parceira, cujo contrato é com ela (#6117 — portal da Gran).
 */
export function pontosDaRetencao(
  resumo: ResumoEconomia,
  brl: (v: number) => string,
  { comAvisoPrevio = true }: { comAvisoPrevio?: boolean } = {},
): PontoRetencao[] {
  const pontos: PontoRetencao[] = [];

  if (resumo.temEconomia) {
    pontos.push({
      titulo: `Você já economizou ${brl(resumo.acumulada)}`,
      texto: resumo.meses > 1
        ? `São ${resumo.meses} faturas com desconto, uma média de ${brl(resumo.media ?? 0)} por mês que fica no seu bolso.`
        : 'Esse valor é o desconto que já entrou na sua conta de luz.',
    });
  } else {
    pontos.push({
      titulo: 'Seu desconto continua valendo',
      texto: 'A economia aparece nas próximas faturas, conforme os créditos de energia são compensados pela distribuidora.',
    });
  }

  pontos.push({
    titulo: 'Desconto anunciado não é o valor final',
    texto: 'Antes de trocar, compare o valor que você paga hoje com o total que a outra empresa vai cobrar, somando a conta da distribuidora.',
  });
  pontos.push({
    titulo: 'A troca pode deixar sua unidade sem desconto por um tempo',
    texto: 'A realocação dos créditos depende do calendário da distribuidora e pode levar alguns ciclos até a nova empresa começar a compensar.',
  });
  if (comAvisoPrevio) {
    pontos.push({
      titulo: 'A saída tem aviso prévio de 90 dias',
      texto: 'É o prazo previsto em contrato, contado a partir do seu pedido. Durante ele, as faturas seguem normalmente.',
    });
  }

  return pontos;
}

// ===========================================================================
// Registro dos passos (medição da retenção)
// ===========================================================================

/**
 * Registra o passo do cliente. Nunca lança: medição não pode atrapalhar quem
 * está tentando cancelar — se falhar, o fluxo segue e só o dado se perde.
 */
export async function registrarPassoRetencao(
  evento: EventoRetencao,
  ucId: number | null,
  economia: number | null,
): Promise<void> {
  try {
    const { supabase } = await import('./supabase');
    await supabase.rpc('registrar_retencao_portal', {
      p_evento: evento,
      p_uc_id: ucId,
      p_economia: economia,
    });
  } catch (e) {
    console.warn('Passo da retenção não registrado:', e);
  }
}
